import { HttpErrorResponse } from '@angular/common/http'
import { inject, Injectable, OnDestroy, signal } from '@angular/core'
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar'
import { errorMessage } from '@mmx/shared'
import { TranslateService } from '@ngx-translate/core'
import { Observable, Subject } from 'rxjs'
import { finalize, takeUntil } from 'rxjs/operators'

import { EstimateBalanceStateService } from './estimate-balance-state.service'

export interface BalanceActionOptions {
  onProgressKey?: string
  onSuccessKey?: string
  onErrorKey?: string
}

// Component-provided (not root) so it shares the host's EstimateBalanceStateService instance and lifetime.
@Injectable()
export class BalanceActionRunner implements OnDestroy {
  // Signal so OnPush views re-render without manual markForCheck().
  readonly busy = signal(false)

  private readonly snackbar = inject(MatSnackBar)
  private readonly translate = inject(TranslateService)
  private readonly state = inject(EstimateBalanceStateService)
  private readonly destroy$ = new Subject<void>()
  private progressRef?: MatSnackBarRef<unknown>

  // `finalize` owns the busy + progress reset so they fire on success, error, AND teardown
  // (destroy$). A source that never completes can't strand the UI busy or the notice open.
  run(op$: Observable<unknown>, opts: BalanceActionOptions = {}): void {
    this.busy.set(true)
    if (opts.onProgressKey) {
      // duration 0 — the pricer can take ~20s, so the notice stays until the outcome replaces it.
      this.progressRef = this.snackbar.open(this.translate.instant(opts.onProgressKey), undefined, { duration: 0 })
    }
    op$.pipe(
      takeUntil(this.destroy$),
      finalize(() => {
        this.busy.set(false)
        this.dismissProgress()
      }),
    ).subscribe({
      next: () => {
        // Dismissed here, not just in finalize, because finalize runs after this handler.
        this.dismissProgress()
        if (opts.onSuccessKey) {
          this.snackbar.open(this.translate.instant(opts.onSuccessKey), undefined, { duration: 2000 })
        }
      },
      error: (err: unknown) => {
        this.dismissProgress()
        if (opts.onErrorKey) {
          const errorKey = err instanceof HttpErrorResponse && err.status === 504
            ? errorMessage(err, opts.onErrorKey)
            : opts.onErrorKey
          this.snackbar.open(this.translate.instant(errorKey), undefined, { duration: 3000 })
        } else {
          this.state.markError(errorMessage(err, 'balance.error'))
        }
      },
    })
  }

  // Raw, pre-i18n error text for callers that branch on the server's message
  // (e.g. detecting an "active modify session" race) rather than a mapped key.
  extractRawMessage(err: unknown): string {
    if (err == null) {
      return ''
    }
    if (err instanceof Error) {
      return err.message
    }
    if (typeof err === 'object' && 'message' in err) {
      const m = (err as { message: unknown }).message
      return typeof m === 'string' ? m : ''
    }
    return ''
  }

  private dismissProgress(): void {
    this.progressRef?.dismiss()
    this.progressRef = undefined
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }
}
